import { AuthService } from "@/auth/AuthService"
import React, { createContext, useEffect, useMemo, ReactNode } from "react"
import { isOfflineAtom } from "./atoms"
import { useStore } from "jotai"
import { useQueryClient } from "@tanstack/react-query"

interface AuthContextType {
  authService: AuthService
}

export const AuthContext = createContext<AuthContextType | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient()
  const store = useStore()

  // ✅ Memoize AuthService to prevent recreation on every render
  // Note: queryClient from useQueryClient() should be stable, but we're being defensive
  const authService = useMemo(() => {
    return new AuthService(queryClient, store)
  }, [queryClient, store])

  useEffect(() => {
    authService.initialize()
  }, [authService])

  // Network status monitoring for offline support
  useEffect(() => {
    const handleOnline = () => store.set(isOfflineAtom, false)
    const handleOffline = () => store.set(isOfflineAtom, true)

    if (typeof window !== "undefined") {
      window.addEventListener("online", handleOnline)
      window.addEventListener("offline", handleOffline)

      return () => {
        window.removeEventListener("online", handleOnline)
        window.removeEventListener("offline", handleOffline)
      }
    }
  }, [store])

  return (
    <AuthContext.Provider value={{ authService }}>
      {children}
    </AuthContext.Provider>
  )
}
